import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { getCalloutPopoverStyles } from '../HBCalloutPopover/HBCalloutPopover.styles';
import HBCalloutPopoverContent, {
    ICalloutPopoverContentProps
} from '../HBCalloutPopover/HBCalloutPopoverContent/HBCalloutPopoverContent';
import HBCornerPopup, {
    HBCornerPopupProps
} from '../HBCornerPopup/HBCornerPopup';
import HBDrawer from '../HBDrawer/HBDrawer';
import { useWindowSize } from '../../hooks/useWindowSize';
import { CalloutContentProps } from '../common/Callout/Callout.types';

/** Props combining HBCornerPopup positioning with HBCalloutPopoverContent display; renders as a drawer on small screens. */
export type CalloutCornerPopupProps = Pick<
    HBCornerPopupProps,
    'isOpen' | 'onClose' | 'placement' | 'width' | 'zIndex'
> &
    Omit<
        ICalloutPopoverContentProps,
        'onClose' | 'stepsContent' | 'title' | 'description'
    > &
    CalloutContentProps & {
        children?: ReactNode;
    };

const HBCalloutCornerPopup = ({
    isOpen,
    onClose,
    testId,
    testIdName,
    calloutTheme = 'neutral',
    primaryActionType = 'button',
    placement = 'right',
    children,
    width,
    zIndex,
    onImageClick,
    withIcon = true,
    ...contentProps
}: CalloutCornerPopupProps & MustIncludeOneOfBasicTestIdProps) => {
    const { isUpToSmall } = useWindowSize();
    const [currStepIndex, setCurrStepIndex] = useState(0);

    const hasSteps = 'steps' in contentProps;

    let title: ReactNode;
    let description: ReactNode;
    let mediaPath: string | object;
    let actions: ICalloutPopoverContentProps['actions'];

    if (hasSteps) {
        const step = contentProps.steps[currStepIndex];
        title = step.title;
        description = step.description;
        mediaPath = step.mediaPath;
        actions = [
            {
                text: step.primaryActionText,
                onClick: e => {
                    const isLastStep =
                        currStepIndex === contentProps.steps.length - 1;
                    const prevIndex = currStepIndex;
                    const newIndex = isLastStep ? 0 : prevIndex + 1;
                    setCurrStepIndex(newIndex);
                    contentProps.onStepChange?.({ prevIndex, newIndex });
                    step.primaryActionOnClick?.();
                    if (isLastStep) {
                        onClose(e);
                    }
                }
            }
        ];
        if ('secondaryActionText' in step) {
            actions.push({
                text: step.secondaryActionText,
                onClick: () => {
                    const prevIndex = currStepIndex;
                    const newIndex = prevIndex - 1;
                    contentProps.onStepChange?.({ prevIndex, newIndex });
                    setCurrStepIndex(newIndex);
                }
            });
        }
    } else {
        title = contentProps.title;
        description = contentProps.description;
        mediaPath = contentProps.mediaPath;
        actions = contentProps.actions;
    }

    if (isUpToSmall) {
        return (
            <HBDrawer
                removeTopPadding={true}
                open={isOpen}
                onClose={onClose as () => void}
            >
                <HBCalloutPopoverContent
                    calloutTheme="neutral"
                    primaryActionType={primaryActionType}
                    actions={actions}
                    testId={testId}
                    testIdName={testIdName}
                    actionsOrientation="vertical"
                    title={title}
                    description={description}
                    mediaPath={mediaPath}
                    onImageClick={onImageClick}
                    withIcon={withIcon}
                    {...(hasSteps && {
                        stepsContent: {
                            currStep: currStepIndex + 1,
                            totalSteps: contentProps.steps.length
                        }
                    })}
                >
                    {children}
                </HBCalloutPopoverContent>
            </HBDrawer>
        );
    }

    return (
        <HBCornerPopup
            isOpen={isOpen}
            onClose={null}
            testId={testId}
            padding={'0'}
            testIdName={testIdName}
            placement={placement}
            width={width}
            zIndex={zIndex}
        >
            <Container style={getCalloutPopoverStyles()[calloutTheme]}>
                <HBCalloutPopoverContent
                    onClose={onClose}
                    calloutTheme={calloutTheme}
                    primaryActionType={primaryActionType}
                    actions={actions}
                    testId={testId}
                    testIdName={testIdName}
                    title={title}
                    description={description}
                    mediaPath={mediaPath}
                    onImageClick={onImageClick}
                    withIcon={withIcon}
                    {...(hasSteps && {
                        stepsContent: {
                            currStep: currStepIndex + 1,
                            totalSteps: contentProps.steps.length
                        }
                    })}
                >
                    {children}
                </HBCalloutPopoverContent>
            </Container>
        </HBCornerPopup>
    );
};

export default HBCalloutCornerPopup;

// -------------------------- Styles -------------------------- //

const Container = styled.div`
    padding: 20px;
`;
